import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api, gameShowWsUrl } from "../lib/api";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { GameShowRoomCanvas } from "../components/game/GameShowRoomCanvas";
import { GameShowItemsPanel } from "../components/game/GameShowItemsPanel";
import { InfoButton } from "../components/game/InfoButton";
import { toast } from "sonner";
import { ArrowLeft, Copy, Disc3, Package, Trash2 } from "lucide-react";

export default function GameShowPage() {
  const { showId } = useParams();
  const { formatApiErrorDetail } = useAuth();
  const navigate = useNavigate();
  const [show, setShow] = useState(null);
  const [characters, setCharacters] = useState([]);
  const [itemCards, setItemCards] = useState([]);
  const [itemsOpen, setItemsOpen] = useState(false);
  const socketRef = useRef(null);

  const loadShow = async () => {
    try {
      const { data } = await api.get(`/game-shows/${showId}`);
      setShow(data);
      const [{ data: chars }, { data: cards }] = await Promise.all([
        api.get("/characters", data.game_id ? { params: { game_id: data.game_id } } : {}),
        data.game_id ? api.get(`/games/${data.game_id}/item-cards`).catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
      ]);
      setCharacters(chars);
      setItemCards(cards);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      navigate("/dashboard");
    }
  };

  useEffect(() => {
    loadShow();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showId]);

  useEffect(() => {
    let socket;
    (async () => {
      const { data } = await api.get("/auth/ws-token");
      socket = new WebSocket(gameShowWsUrl(showId, data.token));
      socketRef.current = socket;
      socket.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          if (msg.type === "gameshow_state") setShow((prev) => ({ ...msg.data, my_role: prev?.my_role, my_participant_id: prev?.my_participant_id }));
        } catch {}
      };
    })();
    return () => socket?.close();
  }, [showId]);

  const isHost = show?.my_role === "host";

  const copyInvite = () => {
    navigator.clipboard.writeText(show.invite_code);
    toast.success("Game Show invite code copied");
  };

  const setStatus = async (status) => {
    await api.patch(`/game-shows/${showId}`, { status });
  };

  const deleteShow = async () => {
    if (!window.confirm(`End "${show.name}"? This can't be undone.`)) return;
    await api.delete(`/game-shows/${showId}`);
    navigate("/dashboard");
  };

  if (!show) return <div className="min-h-screen bg-[#151613] flex items-center justify-center text-[#f1e0c5]/50 text-sm uppercase tracking-widest">Loading Game Show…</div>;

  return (
    <div className="min-h-screen bg-[#151613] text-[#f1e0c5]" data-testid="gameshow-page">
      <nav className="flex items-center justify-between px-6 md:px-12 py-6 border-b border-white/5">
        <Link to="/dashboard" data-testid="gameshow-back-to-dashboard" className="flex items-center gap-2 text-sm text-[#f1e0c5]/60 hover:text-[#3F9142]">
          <ArrowLeft className="w-4 h-4" /> Dashboard
        </Link>
        <div className="flex items-center gap-3">
          <span className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full bg-[#3F9142]/25 text-[#3F9142]" data-testid="gameshow-my-role-badge">{isHost ? "Host" : show.my_role === "spectator" ? "Spectator" : "Contestant"}</span>
          {isHost && (
            <button data-testid="gameshow-copy-invite" onClick={copyInvite} className="flex items-center gap-1.5 text-xs text-[#f1e0c5]/50 hover:text-[#3F9142]">
              <Copy className="w-3.5 h-3.5" /> {show.invite_code}
            </button>
          )}
        </div>
      </nav>

      <div className="px-6 md:px-12 py-10 max-w-[1450px] mx-auto space-y-8">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <h1 className="font-display text-4xl flex items-center gap-3" data-testid="gameshow-name-heading"><Disc3 className="w-8 h-8 text-[#3F9142]" /> {show.name} <InfoButton topic="game_show" /></h1>
          <div className="flex items-center gap-2">
            <Button data-testid="gameshow-items-toggle" variant="secondary" size="sm" className="rounded-full gap-1.5" onClick={() => setItemsOpen(true)}>
              <Package className="w-3.5 h-3.5" /> Items
            </Button>
            {isHost && (
              <>
                <Select value={show.status} onValueChange={setStatus}>
                  <SelectTrigger data-testid="gameshow-status-select" className="bg-black/30 border-white/10 w-36"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="setup">Setup</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
                <Button data-testid="gameshow-delete-button" variant="destructive" size="sm" className="rounded-full gap-1.5" onClick={deleteShow}><Trash2 className="w-3.5 h-3.5" /> End Show</Button>
              </>
            )}
          </div>
        </div>

        <GameShowRoomCanvas
          show={show}
          isHost={isHost}
          characters={characters}
          itemCards={itemCards}
          onLayoutUpdate={(room_layout) => setShow((prev) => ({ ...prev, room_layout }))}
        />

        {show.log?.length > 0 && (
          <div className="panel-wood rounded-2xl p-5 space-y-1.5" data-testid="gameshow-log">
            <h2 className="font-display text-xl mb-1">Activity</h2>
            {[...show.log].reverse().slice(0, 20).map((l) => (
              <p key={l.id} className="text-xs text-[#f1e0c5]/50">{l.text}</p>
            ))}
          </div>
        )}
      </div>

      <Sheet open={itemsOpen} onOpenChange={setItemsOpen}>
        <SheetContent side="right" className="bg-[#1c1712] border-white/10 text-[#f1e0c5] w-full sm:max-w-md overflow-y-auto" data-testid="gameshow-items-drawer">
          <SheetHeader>
            <SheetTitle className="font-display text-xl text-left flex items-center gap-2"><Package className="w-4 h-4 text-[#D4AF37]" /> Items</SheetTitle>
          </SheetHeader>
          <div className="mt-4">
            <GameShowItemsPanel show={show} isHost={isHost} myParticipantId={show.my_participant_id} itemCards={itemCards} />
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
